// UserView.js - Updated with sticky header and tabs
import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaUserCircle,
  FaBriefcase,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaGlobe,
  FaCalendarAlt,
  FaBuilding,
  FaUserTie,
  FaShieldAlt,
  FaFileAlt,
  FaDownload,
  FaIdBadge,
  FaGraduationCap,
  FaLink,
  FaGift,
  FaExclamationTriangle,
  FaCheckDouble,
  FaFileSignature,
  FaUser,
  FaList,
  FaLayerGroup,
  FaClipboardList,
  FaTasks,
} from "react-icons/fa";
import { API_BASE } from "../../../ApiUrl";
import "./UserView.css";
import "./UserViewTabs.css";
import { useParams } from "react-router-dom";
import StatusApproval from "./StatusApproval";
import ResumeModal from "./ResumeModal";
import UserViewStage2 from "./UserViewStage2";
import SystemCalculatedInfo from "./SystemCalculatedInfo";
import UserTabs from "./UserTabs";

const MEDIA_BASE = API_BASE.replace(/\/api\/?$/, "");

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (typeof path === "object") {
    path = path.url || path.file || path.path || null;
    if (!path) return null;
  }
  if (typeof path !== "string") return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${MEDIA_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
};

const STATUS_META = {
  APPROVED: { label: "Approved", color: "approved", icon: <FaCheckDouble /> },
  SUBMITTED: { label: "Submitted", color: "pending", icon: <FaClock /> },
  RETURNED: {
    label: "Returned for Changes",
    color: "returned",
    icon: <FaExclamationTriangle />,
  },
  REJECTED: { label: "Rejected", color: "rejected", icon: <FaTimesCircle /> },
};

const UserView = () => {
  const [user, setUser] = useState(null);
  const [industryLabel, setIndustryLabel] = useState("");
  const [scopeName, setScopeName] = useState("");
  const [showResume, setShowResume] = useState(false);
  const [activeTab, setActiveTab] = useState("stage1");
  const [stage2Status, setStage2Status] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      fetchUser(id);
    }
  }, [id]);

  // Fetch stage2 status when user data is available
  useEffect(() => {
    if (user?.professional_profile?.id) {
      fetchStage2Status(user.professional_profile.id);
    }
  }, [user]);

  const fetchUser = async (id) => {
    try {
      const res = await axios.get(`${API_BASE}/users/${id}/`);
      const userData = res.data.data;
      console.log("user data :- ",userData);
      setUser(userData);

      if (userData.professional_profile?.primary_industry) {
        fetchIndustryLabel(userData.professional_profile.primary_industry);
      }
      if (userData.professional_profile?.primary_scope) {
        fetchScopeName(userData.professional_profile.primary_scope);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchIndustryLabel = async (industryId) => {
    try {
      const res = await axios.get(
        `${API_BASE}/reference-values/${industryId}/`,
      );
      if (res.data.success && res.data.data) {
        setIndustryLabel(res.data.data.label);
      }
    } catch (err) {
      console.error("Error fetching industry label:", err);
    }
  };

  const fetchScopeName = async (scopeId) => {
    try {
      const res = await axios.get(`${API_BASE}/scope-catalogs/${scopeId}/`);
      if (res.data.success && res.data.data) {
        setScopeName(res.data.data.scope_name);
      }
    } catch (err) {
      console.error("Error fetching scope name:", err);
    }
  };

  const fetchStage2Status = async (professionalId) => {
    try {
      const res = await axios.get(
        `${API_BASE}/professional-profiles/${professionalId}/stage2_status/`,
      );
      if (res.data.success) {
        setStage2Status(res.data.data);
        console.log("Stage 2 Status:", res.data.data);
      }
    } catch (err) {
      console.error("Error fetching stage2 status:", err);
    }
  };

  if (!user) {
    return (
      <div className="up-loading-container">
        <div className="up-loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  const profile = user.professional_profile || {};
  console.log("profile=", profile);
  console.log("profile_status=", profile.profile_status);

  const application = user.registration_applications?.[0] || {};
  const applicationId = application.id || null;
  const snapshot = application.stage1_snapshot || {};
  const consents = user.consent_records || [];

  const getConsent = (type) => {
    const item = consents.find((c) => c.consent_type === type);
    return item?.is_granted;
  };

  const getInitials = () => {
    const first = profile.first_name?.charAt(0) || "";
    const last = profile.last_name?.charAt(0) || "";
    return (first + last).toUpperCase();
  };

  const applicationStatus = application.status || "";
  const statusMeta = STATUS_META[applicationStatus] || {
    label: applicationStatus || "N/A",
    color: "pending",
    icon: <FaFileAlt />,
  };

  const evidenceDocuments = user.evidence_documents || [];
  const resumeDoc = evidenceDocuments.find(
    (doc) =>
      /resume/i.test(doc.file || "") ||
      doc.mime_type === "application/pdf" ||
      /\.(pdf|docx?)$/i.test(doc.original_file_name || ""),
  );
  const photoDoc = evidenceDocuments.find(
    (doc) =>
      /photograph|photo/i.test(doc.file || "") ||
      /^image\//i.test(doc.mime_type || ""),
  );
  const resumeUrl = resolveFileUrl(resumeDoc?.file);
  const photoUrl = resolveFileUrl(photoDoc?.file);

  const fullName = [
    profile.first_name || snapshot.first_name,
    profile.middle_name || snapshot.middle_name,
    profile.last_name || snapshot.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "—";

  // Extract header and banner as a separate render function
  const renderHeaderAndBanner = () => (
    <>
      <div className="user-profile-header">
        <div className="user-avatar-wrapper">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={profile.display_name || "Profile"}
              className="user-avatar-photo"
            />
          ) : (
            <div className="user-avatar">
              {getInitials() || <FaUserCircle size={30} />}
            </div>
          )}
          <div className="user-status-badge">
            <span
              className={`status-dot ${user.is_active ? "active" : "inactive"}`}
            ></span>
          </div>
        </div>

        <div className="user-header-info">
          <h1 className="user-display-name">
            {profile.display_name || fullName || "User"}
          </h1>
          <p className="user-job-title">
            <FaBriefcase className="header-icon" />
            {profile.current_job_title ||
              snapshot.current_job_title ||
              "Professional"}
          </p>
          <div className="user-meta-tags">
            <span className="meta-tag">
              <FaClock className="tag-icon" />
              Member since {formatDate(user.date_joined)}
            </span>
            <span
              className={`meta-tag status-pill status-pill--${statusMeta.color}`}
            >
              {statusMeta.icon}
              {statusMeta.label}
            </span>
          </div>
        </div>

        <div className="user-header-actions">
          <button
            type="button"
            className="generate-resume-btn"
            onClick={() => setShowResume(true)}
          >
            <FaFileSignature />
            Generate Resume
          </button>
        </div>

        {applicationStatus &&
          applicationStatus !== "APPROVED" &&
          applicationId && (
            <StatusApproval
              applicationId={applicationId}
              onSuccess={() => fetchUser(id)}
            />
          )}
      </div>

      {(applicationStatus === "RETURNED" ||
        applicationStatus === "REJECTED") && (
        <div className={`review-banner review-banner--${statusMeta.color}`}>
          <div className="review-banner__icon">{statusMeta.icon}</div>
          <div className="review-banner__body">
            <h4>
              Application {statusMeta.label}
              {application.reviewed_at && (
                <span className="review-banner__date">
                  {" "}
                  · {formatDate(application.reviewed_at)}
                </span>
              )}
            </h4>
            <p>
              {application.decision_reason ||
                "No reason was provided by the reviewer."}
            </p>
            {applicationStatus === "RETURNED" && (
              <p className="review-banner__hint">
                Please update the highlighted details and resubmit the
                application for review.
              </p>
            )}
          </div>
        </div>
      )}

      {applicationStatus === "APPROVED" && (
        <div className="review-banner review-banner--approved">
          <div className="review-banner__icon">{statusMeta.icon}</div>
          <div className="review-banner__body">
            <h4>
              Application Approved
              {application.reviewed_at && (
                <span className="review-banner__date">
                  {" "}
                  · {formatDate(application.reviewed_at)}
                </span>
              )}
            </h4>
            <p>
              Stage 1 registration has been approved. Stage 2 can be completed
              to unlock full project matching.
            </p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="user-profile-tabs">
        <button
          className={`tab-btn ${activeTab === "stage1" ? "active" : ""}`}
          onClick={() => setActiveTab("stage1")}
        >
          <FaUser className="tab-icon" />
          Basic Info
        </button>

        {/* Show Professional Info tab only if Stage 2 is submitted or status exists */}
        {stage2Status?.profile_status && (
          <button
            className={`tab-btn ${activeTab === "stage2" ? "active" : ""}`}
            onClick={() => setActiveTab("stage2")}
          >
            <FaTasks className="tab-icon" />
            Professional Info
          </button>
        )}
         {stage2Status?.profile_status && (
          <button
            className={`tab-btn ${activeTab === "system" ? "active" : ""}`}
            onClick={() => setActiveTab("system")}
          >
            <FaTasks className="tab-icon" />
            System Calculated Info
          </button>
        )}
      </div>
    </>
  );

  const renderStage1Content = () => (
    <div className="user-profile-grid">
      <div className="user-card account-card">
        <div className="card-header">
          <div className="card-icon-wrapper account-icon">
            <FaUserCircle />
          </div>
          <h3>Account Details</h3>
        </div>
        <div className="card-body">
          <div className="info-item">
            <FaEnvelope className="info-icon" />
            <div className="info-content">
              <span className="info-label">Email</span>
              <span className="info-value">{user.email}</span>
            </div>
          </div>
          <div className="info-item">
            <FaPhone className="info-icon" />
            <div className="info-content">
              <span className="info-label">Phone</span>
              <span className="info-value">
                {user.mobile_country_code} {user.mobile_number}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaShieldAlt className="info-icon" />
            <div className="info-content">
              <span className="info-label">Two-Factor Method</span>
              <span className="info-value">{user.mfa_method || "—"}</span>
            </div>
          </div>
          <div className="info-item">
            <FaCalendarAlt className="info-icon" />
            <div className="info-content">
              <span className="info-label">Joined</span>
              <span className="info-value">
                {formatDate(user.date_joined)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="user-card identity-card">
        <div className="card-header">
          <div className="card-icon-wrapper identity-icon">
            <FaIdBadge />
          </div>
          <h3>Identity</h3>
        </div>
        <div className="card-body">
          <div className="info-item">
            <FaUserCircle className="info-icon" />
            <div className="info-content">
              <span className="info-label">Full Name</span>
              <span className="info-value">{fullName || "—"}</span>
            </div>
          </div>
          <div className="info-item">
            <FaUserCircle className="info-icon" />
            <div className="info-content">
              <span className="info-label">Preferred Name</span>
              <span className="info-value">
                {profile.preferred_name || snapshot.preferred_name || "—"}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaIdBadge className="info-icon" />
            <div className="info-content">
              <span className="info-label">Name Display Order</span>
              <span className="info-value">
                {profile.name_display_order ||
                  snapshot.name_display_order ||
                  "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="user-card location-card">
        <div className="card-header">
          <div className="card-icon-wrapper location-icon">
            <FaMapMarkerAlt />
          </div>
          <h3>Location</h3>
        </div>
        <div className="card-body">
          <div className="info-item">
            <FaMapMarkerAlt className="info-icon" />
            <div className="info-content">
              <span className="info-label">City</span>
              <span className="info-value">
                {profile.city || snapshot.city || "Not specified"}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaGlobe className="info-icon" />
            <div className="info-content">
              <span className="info-label">Country</span>
              <span className="info-value">
                {profile.country_of_residence ||
                  snapshot.country_of_residence ||
                  "Not specified"}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaClock className="info-icon" />
            <div className="info-content">
              <span className="info-label">Timezone</span>
              <span className="info-value">
                {profile.timezone || snapshot.time_zone || "Not specified"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="user-card professional-card">
        <div className="card-header">
          <div className="card-icon-wrapper professional-icon">
            <FaUserTie />
          </div>
          <h3>Professional Direction</h3>
        </div>
        <div className="card-body">
          <div className="info-item">
            <FaBuilding className="info-icon" />
            <div className="info-content">
              <span className="info-label">Industry</span>
              <span className="info-value">
                {industryLabel || profile.primary_industry || "—"}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaBriefcase className="info-icon" />
            <div className="info-content">
              <span className="info-label">Scope</span>
              <span className="info-value">
                {scopeName || profile.primary_scope || "—"}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaUserTie className="info-icon" />
            <div className="info-content">
              <span className="info-label">Career Stage</span>
              <span className="info-value">
                {profile.self_declared_career_stage ||
                  snapshot.self_declared_career_stage ||
                  "—"}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaBriefcase className="info-icon" />
            <div className="info-content">
              <span className="info-label">Current Job Title</span>
              <span className="info-value">
                {profile.current_job_title ||
                  snapshot.current_job_title ||
                  "—"}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaClock className="info-icon" />
            <div className="info-content">
              <span className="info-label">Experience Band</span>
              <span className="info-value">
                {profile.initial_experience_band ||
                  snapshot.initial_experience_band ||
                  "—"}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaGraduationCap className="info-icon" />
            <div className="info-content">
              <span className="info-label">Highest Qualification</span>
              <span className="info-value">
                {profile.highest_qualification_level ||
                  snapshot.highest_qualification_level ||
                  "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="user-card documents-card">
        <div className="card-header">
          <div className="card-icon-wrapper documents-icon">
            <FaFileAlt />
          </div>
          <h3>Documents</h3>
        </div>
        <div className="card-body">
          <div className="document-row">
            <div className="document-row__label">
              <FaFileAlt className="info-icon" />
              <span>Resume</span>
            </div>
            {resumeUrl ? (
              <a
                href={resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="document-download-btn"
              >
                <FaDownload /> Download
              </a>
            ) : (
              <span className="document-empty">Not uploaded</span>
            )}
          </div>

          <div className="document-row">
            <div className="document-row__label">
              <FaUserCircle className="info-icon" />
              <span>Profile Photograph</span>
            </div>
            {photoUrl ? (
              <a
                href={photoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="document-download-btn"
              >
                <FaDownload /> View
              </a>
            ) : (
              <span className="document-empty">Not uploaded</span>
            )}
          </div>
        </div>
      </div>

      <div className="user-card application-card">
        <div className="card-header">
          <div className="card-icon-wrapper application-icon">
            <FaFileAlt />
          </div>
          <h3>Application</h3>
        </div>
        <div className="card-body">
          <div className="info-item">
            <FaFileAlt className="info-icon" />
            <div className="info-content">
              <span className="info-label">Status</span>
              <span
                className={`info-value status-text status-text--${statusMeta.color}`}
              >
                {statusMeta.label}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaCalendarAlt className="info-icon" />
            <div className="info-content">
              <span className="info-label">Submitted</span>
              <span className="info-value">
                {formatDate(application.submitted_at)}
              </span>
            </div>
          </div>
          {application.reviewed_at && (
            <div className="info-item">
              <FaCalendarAlt className="info-icon" />
              <div className="info-content">
                <span className="info-label">Reviewed</span>
                <span className="info-value">
                  {formatDate(application.reviewed_at)}
                </span>
              </div>
            </div>
          )}
          {application.decision_reason && (
            <div className="info-item">
              <FaExclamationTriangle className="info-icon" />
              <div className="info-content">
                <span className="info-label">Reviewer Note</span>
                <span className="info-value">
                  {application.decision_reason}
                </span>
              </div>
            </div>
          )}
          <div className="info-item">
            <FaMapMarkerAlt className="info-icon" />
            <div className="info-content">
              <span className="info-label">Country</span>
              <span className="info-value">
                {snapshot.country_of_residence || "Not specified"}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaMapMarkerAlt className="info-icon" />
            <div className="info-content">
              <span className="info-label">City</span>
              <span className="info-value">
                {snapshot.city || "Not specified"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="user-card referral-card">
        <div className="card-header">
          <div className="card-icon-wrapper referral-icon">
            <FaGift />
          </div>
          <h3>Referral</h3>
        </div>
        <div className="card-body">
          <div className="info-item">
            <FaLink className="info-icon" />
            <div className="info-content">
              <span className="info-label">Source</span>
              <span className="info-value">
                {user.referral_source || snapshot.referral_source || "—"}
              </span>
            </div>
          </div>
          <div className="info-item">
            <FaGift className="info-icon" />
            <div className="info-content">
              <span className="info-label">Code</span>
              <span className="info-value">
                {user.referral_code || snapshot.referral_code || "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="user-card consents-card">
        <div className="card-header">
          <div className="card-icon-wrapper consents-icon">
            <FaShieldAlt />
          </div>
          <h3>Consents</h3>
        </div>
        <div className="card-body">
          <div className="consent-item">
            <span className="consent-label">Terms of Service</span>
            <span
              className={`consent-status ${getConsent("TERMS") ? "granted" : "denied"}`}
            >
              {getConsent("TERMS") ? <FaCheckCircle /> : <FaTimesCircle />}
              {getConsent("TERMS") ? " Granted" : " Denied"}
            </span>
          </div>
          <div className="consent-item">
            <span className="consent-label">Privacy Policy</span>
            <span
              className={`consent-status ${getConsent("PRIVACY") ? "granted" : "denied"}`}
            >
              {getConsent("PRIVACY") ? <FaCheckCircle /> : <FaTimesCircle />}
              {getConsent("PRIVACY") ? " Granted" : " Denied"}
            </span>
          </div>
          <div className="consent-item">
            <span className="consent-label">Resume Processing</span>
            <span
              className={`consent-status ${getConsent("RESUME_PROCESSING") ? "granted" : "denied"}`}
            >
              {getConsent("RESUME_PROCESSING") ? (
                <FaCheckCircle />
              ) : (
                <FaTimesCircle />
              )}
              {getConsent("RESUME_PROCESSING") ? " Granted" : " Denied"}
            </span>
          </div>
          <div className="consent-item">
            <span className="consent-label">Marketing</span>
            <span
              className={`consent-status ${getConsent("MARKETING") ? "granted" : "denied"}`}
            >
              {getConsent("MARKETING") ? (
                <FaCheckCircle />
              ) : (
                <FaTimesCircle />
              )}
              {getConsent("MARKETING") ? " Granted" : " Denied"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Header />

        <div className="user-profile-container">
       
  {/* Fixed Header Section */}
  <div className="user-profile-sticky-wrapper">
    {renderHeaderAndBanner()}
  </div>

  {/* Scrollable Content */}
  <div className="user-profile-tab-content-wrapper">
    <div className="user-profile-tab-content">
      {activeTab === "stage1" && renderStage1Content()}

      {activeTab === "stage2" && (
        <UserViewStage2
          professionalId={profile.id}
          applicationId={applicationId}
          onRefresh={() => fetchUser(id)}
          stage2Status={stage2Status}
        />
      )}

      {activeTab === "system" && (
        <SystemCalculatedInfo professionalId={profile.id} />
      )}
    </div>
  </div>
</div>
      </div>

      {showResume && (
        <ResumeModal
          user={user}
          industryLabel={industryLabel}
          scopeName={scopeName}
          onClose={() => setShowResume(false)}
        />
      )}
    </div>
  );
};

export default UserView;